import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

interface DeleteWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  workspaceName: string;
  isDeleting: boolean;
  isGlobalDarkMode?: boolean;
}

export const DeleteWorkspaceDialog: React.FC<DeleteWorkspaceDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  workspaceName,
  isDeleting,
  isGlobalDarkMode = true,
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleInitialConfirm = () => {
    setShowConfirmation(true);
  };

  const handleFinalConfirm = async () => {
    if (confirmationText === workspaceName) {
      await onConfirm();
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setShowConfirmation(false);
      setConfirmationText('');
      setCopied(false);
      onClose();
    }
  };

  const handleBack = () => {
    setShowConfirmation(false);
    setConfirmationText('');
    setCopied(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(workspaceName);
      setCopied(true);
      toast.success('Workspace name copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const isConfirmationValid = confirmationText === workspaceName;

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent className={`max-w-md ${isGlobalDarkMode
        ? 'bg-gray-900 text-white border-gray-700'
        : 'bg-white text-gray-900 border-gray-200'
        }`}>
        {!showConfirmation ? (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-full ${isGlobalDarkMode ? 'bg-red-900/20' : 'bg-red-100'
                  }`}>
                  <AlertTriangle className={`w-6 h-6 ${isGlobalDarkMode ? 'text-red-500' : 'text-red-600'
                    }`} />
                </div>
                <AlertDialogTitle className={`text-xl ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Delete Workspace?</AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className={`text-base space-y-3 ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                  <p className={`font-semibold ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                    You are about to delete "{workspaceName}"
                  </p>
                  <p>This will permanently delete:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                    <li>All workspace data</li>
                    <li>All brands and content</li>
                    <li>All team members and invitations</li>
                    <li>All files and assets</li>
                    <li>All settings and customizations</li>
                  </ul>
                  <p className={`font-semibold ${isGlobalDarkMode ? 'text-red-500' : 'text-red-600'
                    }`}>
                    This action cannot be undone.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isDeleting}
                autoFocus
                className={
                  isGlobalDarkMode
                    ? '!text-white !border-gray-600 hover:!bg-gray-800 hover:!text-white'
                    : '!text-gray-900 !border-gray-300 hover:!bg-gray-100'
                }
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleInitialConfirm}
                disabled={isDeleting}
              >
                Continue
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-full ${isGlobalDarkMode ? 'bg-red-900/20' : 'bg-red-100'
                  }`}>
                  <AlertTriangle className={`w-6 h-6 ${isGlobalDarkMode ? 'text-red-500' : 'text-red-600'
                    }`} />
                </div>
                <AlertDialogTitle className={`text-xl ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Confirm Deletion</AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className={`text-base space-y-4 ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <p>
                    To confirm, please type the workspace name exactly as shown below:
                  </p>
                  <div className={`p-3 rounded-md flex items-center justify-between gap-3 ${isGlobalDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <code className={`text-sm font-mono font-semibold break-all ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {workspaceName}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopyToClipboard}
                      disabled={isDeleting}
                      className={`flex-shrink-0 p-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isGlobalDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                      title="Copy workspace name"
                    >
                      {copied ? (
                        <Check className={`w-4 h-4 ${isGlobalDarkMode ? 'text-green-500' : 'text-green-600'}`} />
                      ) : (
                        <Copy className={`w-4 h-4 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                      )}
                    </button>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmation" className={isGlobalDarkMode ? 'text-white' : 'text-gray-900'}>Workspace name</Label>
                    <Input
                      id="confirmation"
                      type="text"
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      placeholder="Type workspace name here"
                      disabled={isDeleting}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && isConfirmationValid && !isDeleting) {
                          handleFinalConfirm();
                        }
                      }}
                      className={`${isGlobalDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${confirmationText && !isConfirmationValid ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    />
                    {confirmationText && !isConfirmationValid && (
                      <p className={`text-sm ${isGlobalDarkMode ? 'text-red-500' : 'text-red-600'}`}>
                        Workspace name does not match
                      </p>
                    )}
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isDeleting}
                className={
                  isGlobalDarkMode
                    ? '!text-white !border-gray-600 hover:!bg-gray-800 hover:!text-white'
                    : '!text-gray-900 !border-gray-300 hover:!bg-gray-100'
                }
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleFinalConfirm}
                disabled={!isConfirmationValid || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Workspace'}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
